
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

type Produto = {
  id: string;
  nome: string;
  descricao: string;
  tipo: 'curso' | 'ebook';
  preco: number;
  imagem_url: string;
  categoria: string;
  duracao: string;
  nivel: string;
  created_at: string;
  ativo: boolean;
};

export default function ProdutoDetalhesPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [produto, setProduto] = useState<Produto | null>(null);
  const [loading, setLoading] = useState(true);
  const [comprando, setComprando] = useState(false);
  const [outrosProdutos, setOutrosProdutos] = useState<Produto[]>([]);

  useEffect(() => {
    if (id) {
      carregarProduto();
      carregarOutrosProdutos();
    }
  }, [id]);

  const carregarProduto = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('produtos')
        .select('*')
        .eq('id', id)
        .eq('ativo', true)
        .single();

      if (error) {
        console.error('Erro ao carregar produto:', error);
        navigate('/produtos');
        return;
      }

      setProduto(data);
    } catch (error) {
      console.error('Erro ao carregar produto:', error);
      navigate('/produtos');
    } finally {
      setLoading(false);
    }
  };

  const carregarOutrosProdutos = async () => {
    try {
      const { data, error } = await supabase
        .from('produtos')
        .select('*')
        .eq('ativo', true)
        .neq('id', id)
        .order('created_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      setOutrosProdutos(data || []);
    } catch (error) {
      console.error('Erro ao carregar outros produtos:', error);
    }
  };

  const handleComprar = async () => {
    if (!user) {
      alert('Por favor, faça login para comprar este produto');
      navigate('/login');
      return;
    }

    if (!produto) return;

    setComprando(true);

    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          produtoId: produto.id,
          userId: user.id,
          preco: produto.preco,
          nome: produto.nome,
          imagem: produto.imagem_url,
          tipo: produto.tipo, // ✅ Agora incluímos o tipo do produto
        },
      });

      if (error) throw error;

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error: any) {
      console.error('Erro ao criar checkout:', error);
      alert('Erro ao processar pagamento: ' + error.message);
    } finally {
      setComprando(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400"></div>
          <p className="text-gray-400 mt-4">A carregar produto...</p>
        </div>
      </div>
    );
  }

  if (!produto) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-6xl text-gray-700 mb-4"></i>
          <h1 className="text-2xl font-bold text-white mb-2">Produto não encontrado</h1>
          <p className="text-gray-400 mb-6">O produto que procura não existe ou foi removido</p>
          <button
            onClick={() => navigate('/produtos')}
            className="px-6 py-3 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-lg hover:from-yellow-500 hover:to-yellow-700 transition-all cursor-pointer whitespace-nowrap"
          >
            Ver Produtos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      <main className="pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <motion.nav
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 text-sm text-gray-400 mb-8"
          >
            <button
              onClick={() => navigate('/')}
              className="hover:text-yellow-400 transition-colors cursor-pointer"
            >
              Início
            </button>
            <i className="ri-arrow-right-s-line"></i>
            <button
              onClick={() => navigate('/produtos')}
              className="hover:text-yellow-400 transition-colors cursor-pointer"
            >
              Produtos
            </button>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">{produto.nome}</span>
          </motion.nav>

          {/* Detalhes do Produto */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
            {/* Imagem */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="relative w-full h-96 lg:h-[600px] rounded-2xl overflow-hidden bg-gradient-to-br from-gray-900 to-black border border-gray-800">
                <img
                  src={produto.imagem_url}
                  alt={produto.nome}
                  className="w-full h-full object-contain bg-gray-900"
                />
                <div className="absolute top-6 right-6">
                  <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                    produto.tipo === 'curso'
                      ? 'bg-yellow-400 text-black'
                      : 'bg-gray-400 text-black'
                  }`}>
                    {produto.tipo === 'curso' ? 'CURSO' : 'E-BOOK'}
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Informações */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-8"
            >
              {/* Categoria e Nível */}
              <div className="flex items-center gap-4">
                <span className="px-4 py-2 bg-yellow-400/20 text-yellow-400 text-sm font-semibold rounded-full">
                  {produto.categoria}
                </span>
                <span className="px-4 py-2 bg-gray-800 text-gray-300 text-sm font-semibold rounded-full">
                  {produto.nivel}
                </span>
              </div>

              {/* Título */}
              <div>
                <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
                  {produto.nome}
                </h1>
                <div className="flex items-center gap-4 text-gray-400">
                  <div className="flex items-center gap-2">
                    <i className="ri-time-line"></i>
                    <span>{produto.duracao}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-calendar-line"></i>
                    <span>Acesso vitalício</span>
                  </div>
                </div>
              </div>

              {/* Descrição */}
              <div>
                <h3 className="text-xl font-bold text-white mb-4">Sobre este {produto.tipo}</h3>
                <p className="text-gray-400 leading-relaxed text-lg">
                  {produto.descricao}
                </p>
              </div>

              {/* Características */}
              <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">O que está incluído:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <i className="ri-checkbox-circle-fill text-green-400 text-xl"></i>
                    <span className="text-gray-300">Conteúdo premium</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-checkbox-circle-fill text-green-400 text-xl"></i>
                    <span className="text-gray-300">Acesso vitalício</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-checkbox-circle-fill text-green-400 text-xl"></i>
                    <span className="text-gray-300">Suporte especializado</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-checkbox-circle-fill text-green-400 text-xl"></i>
                    <span className="text-gray-300">Certificado de conclusão</span>
                  </div>
                </div>
              </div>

              {/* Preço e Compra */}
              <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-2xl p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-5xl font-bold text-white mb-2">
                      R${produto.preco.toFixed(2)}
                    </p>
                    <p className="text-gray-400">Pagamento único</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 text-yellow-400 mb-2">
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                    </div>
                    <p className="text-sm text-gray-400">Avaliação 5.0</p>
                  </div>
                </div>

                <button
                  onClick={handleComprar}
                  disabled={comprando}
                  className="w-full px-8 py-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold text-lg rounded-xl hover:from-yellow-500 hover:to-yellow-700 transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap"
                >
                  {comprando ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      A processar...
                    </>
                  ) : (
                    <>
                      <i className="ri-shopping-cart-line mr-2"></i>
                      Comprar Agora
                    </>
                  )}
                </button>

                <div className="flex items-center justify-center gap-4 mt-4 text-sm text-gray-400">
                  <div className="flex items-center gap-2">
                    <i className="ri-shield-check-line text-green-400"></i>
                    <span>Compra segura</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-refund-line text-green-400"></i>
                    <span>Garantia 7 dias</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Produtos Relacionados */}
          {outrosProdutos.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <h2 className="text-3xl font-bold text-white mb-8">Outros Produtos</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-product-shop>
                {outrosProdutos.map((outroProduto, index) => (
                  <motion.div
                    key={outroProduto.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-2xl overflow-hidden hover:border-yellow-400/50 transition-all group cursor-pointer"
                    onClick={() => navigate(`/produto/${outroProduto.id}`)}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={outroProduto.imagem_url}
                        alt={outroProduto.nome}
                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                      />
                      <span className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold ${
                        outroProduto.tipo === 'curso'
                          ? 'bg-yellow-400 text-black'
                          : 'bg-gray-400 text-black'
                      }`}>
                        {outroProduto.tipo === 'curso' ? 'CURSO' : 'E-BOOK'}
                      </span>
                    </div>

                    <div className="p-6">
                      <h3 className="text-lg font-bold text-white mb-2 group-hover:text-yellow-400 transition-colors">
                        {outroProduto.nome}
                      </h3>
                      <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                        {outroProduto.descricao}
                      </p>
                      <div className="flex items-center justify-between">
                        <p className="text-xl font-bold text-yellow-400">
                          R${outroProduto.preco.toFixed(2)}
                        </p>
                        <span className="text-yellow-400 text-sm">
                          Ver detalhes →
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.section>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
